// MailboxSwitcher — dropdown at the top of the EMAIL sidebar that
// lets advisors switch between their own mailbox and shared
// mailboxes they have FullAccess on.
//
// The list of shared mailboxes is fetched from
// /api/email/shared-mailboxes (server-side hardcoded registry).
// User pick persists in localStorage via useSelectedMailbox so the
// choice carries across reloads + tabs.
//
// Switching to a shared mailbox lets the user see folders + messages
// + attachments. Sending FROM a shared mailbox is also supported now
// (v1.x phase 11.8 completion): the record composer's From-picker and
// the reading-pane reply composer both send AS the selected mailbox
// via Mail.Send.Shared. This still requires FullAccess + Send-As in
// Exchange admin — Graph 403s otherwise.
//
// If the advisor doesn't have FullAccess on a mailbox in Exchange
// admin, the Graph call will 403. The folder list / message list
// will surface the error gracefully ("Couldn't load folders").
// Stewardship OS doesn't try to pre-validate access; trust the
// server-side response.

import { useEffect, useState } from "react";
import { ChevronDown, Mail, MailPlus } from "lucide-react";

import { api } from "../../api";
import { useSelectedMailbox } from "../../utils/email-selected-mailbox";
import { useToast } from "../Toast";

interface SharedMailboxOption {
  upn: string;
  display_name: string;
  description: string;
}

export interface MailboxSwitcherProps {
  // Compact mode hides the description sublines + uses a smaller
  // pill style. Used inside the collapsed sidebar.
  collapsed?: boolean;
}

export function MailboxSwitcher({ collapsed = false }: MailboxSwitcherProps) {
  const toast = useToast();
  const { selectedMailbox, setMailbox } = useSelectedMailbox();
  const [options, setOptions] = useState<SharedMailboxOption[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await api.getSharedMailboxes();
        if (cancelled) return;
        setOptions(resp.mailboxes);
      } catch {
        // Silent fallback — sidebar still works for the user's own
        // mailbox even if the registry endpoint hiccups.
        if (!cancelled) setOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentLabel = (() => {
    if (!selectedMailbox) return "My mailbox";
    const opt = options?.find((m) => m.upn === selectedMailbox);
    return opt?.display_name ?? selectedMailbox;
  })();

  const handlePick = (upn: string | null, label: string) => {
    setMailbox(upn);
    setOpen(false);
    toast.success(`Switched to ${label}.`);
  };

  if (collapsed) {
    // Compact: just an icon button that opens the picker. Position
    // matches the sidebar's collapsed-mode density.
    return (
      <div className="mailbox-switcher mailbox-switcher-collapsed">
        <button
          type="button"
          className="mailbox-switcher-trigger-collapsed"
          onClick={() => setOpen((v) => !v)}
          title={`Mailbox: ${currentLabel}`}
          aria-label={`Switch mailbox (current: ${currentLabel})`}
        >
          {selectedMailbox ? (
            <MailPlus size={16} strokeWidth={1.5} aria-hidden />
          ) : (
            <Mail size={16} strokeWidth={1.5} aria-hidden />
          )}
        </button>
        {open ? (
          <MailboxSwitcherDropdown
            options={options ?? []}
            selectedMailbox={selectedMailbox}
            onPick={handlePick}
            onClose={() => setOpen(false)}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="mailbox-switcher">
      <button
        type="button"
        className="mailbox-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Switch mailbox"
      >
        <span className="mailbox-switcher-trigger-eyebrow">MAILBOX</span>
        <span className="mailbox-switcher-trigger-label">
          {currentLabel}
        </span>
        <span className="mailbox-switcher-chevron" aria-hidden>
          <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
        </span>
      </button>
      {open ? (
        <MailboxSwitcherDropdown
          options={options ?? []}
          selectedMailbox={selectedMailbox}
          onPick={handlePick}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

interface MailboxSwitcherDropdownProps {
  options: SharedMailboxOption[];
  selectedMailbox: string | null;
  onPick: (upn: string | null, label: string) => void;
  onClose: () => void;
}

function MailboxSwitcherDropdown({
  options,
  selectedMailbox,
  onPick,
  onClose,
}: MailboxSwitcherDropdownProps) {
  // Click-outside to close.
  useEffect(() => {
    const win = (
      globalThis as unknown as {
        window?: {
          addEventListener: (e: string, fn: () => void) => void;
          removeEventListener: (e: string, fn: () => void) => void;
        };
      }
    ).window;
    if (!win) return;
    const handler = () => onClose();
    // Defer the listener attachment by one tick so the click that
    // OPENED the dropdown doesn't immediately close it.
    const timer = setTimeout(() => {
      win.addEventListener("click", handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      win.removeEventListener?.("click", handler);
    };
  }, [onClose]);

  return (
    <ul
      className="mailbox-switcher-dropdown"
      role="listbox"
      onClick={(e) => e.stopPropagation()}
    >
      <li>
        <button
          type="button"
          className={`mailbox-switcher-option${
            selectedMailbox === null ? " is-selected" : ""
          }`}
          onClick={() => onPick(null, "your own mailbox")}
        >
          <span className="mailbox-switcher-option-name">My mailbox</span>
          <span className="mailbox-switcher-option-description">
            Your own Office 365 inbox
          </span>
        </button>
      </li>
      {options.map((opt) => (
        <li key={opt.upn}>
          <button
            type="button"
            className={`mailbox-switcher-option${
              selectedMailbox === opt.upn ? " is-selected" : ""
            }`}
            onClick={() => onPick(opt.upn, opt.display_name)}
          >
            <span className="mailbox-switcher-option-name">
              {opt.display_name}
            </span>
            <span className="mailbox-switcher-option-description">
              {opt.description}
            </span>
            <span className="mailbox-switcher-option-upn">{opt.upn}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
